import { ChangeDetectionStrategy, Component, inject, OnInit, Signal, ViewEncapsulation } from "@angular/core";
/* eslint-disable-next-line @tseslint/no-shadow */
import { Event, NavigationEnd, Router } from "@angular/router";
import { BehaviorSubject, filter, pairwise, startWith } from "rxjs";
import { FaIconLibrary } from "@fortawesome/angular-fontawesome";
import { faBell, faQuestionCircle } from "@fortawesome/pro-light-svg-icons";
import { HeaderDropdownMenuComponent } from "./components/dropdown-menu/dropdown-menu.component";
import { IconModule } from "@ogs-gmbh/ngx-m3-components";
import { MatToolbarModule } from "@angular/material/toolbar";
import { AsyncPipe } from "@angular/common";
import { MatButtonModule } from "@angular/material/button";
import { provideTranslationScope, TranslationPipeModule } from "@ogs-gmbh/ngx-translate";
import { KeycloakService } from "@ogs/ngx-keycloak";
import { PROJECT_ROLE_RESOURCE, PROJECT_ROLES } from "@shared/types/roles/project-roles.types";
import { Permission } from "@shared/types/permission.types";
import { RestrictedDirective } from "@shared/directives/restricted/restricted.directive";
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ProcessService } from "@core/services/process/process.service";
import { toSignal } from "@angular/core/rxjs-interop";
import { NotificationMenuService } from "@core/components/notification/notification-menu/services/notification-menu.service";
import { MatBadgeModule } from '@angular/material/badge';
import { CUSTOMER_ROLE_RESOURCE, CUSTOMER_ROLES } from "@shared/types/roles/customer-roles-types";
import { ACCESS_CONTROL_ROLE_RESOURCE, ACCESS_CONTROL_ROLES } from "@shared/types/roles/access-control-roles.type";

interface Button {
  defaultText: string;
  token: string;
  path: string;
  permissions?: Permission[];
  isDisabled?: boolean | undefined;
  isActive?: boolean | undefined;
}

@Component({
  selector: "app-core-ui-header",
  templateUrl: "./header.component.html",
  styleUrl: "header.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.Emulated,
  standalone: true,
  imports: [ HeaderDropdownMenuComponent,
    IconModule,
    MatToolbarModule,
    MatButtonModule,
    AsyncPipe,
    TranslationPipeModule,
    RestrictedDirective,
    MatProgressSpinnerModule,
    MatBadgeModule ],
  providers: [ provideTranslationScope("feature/secure") ]
})
export class HeaderComponent implements OnInit {
  private readonly _notificationMenuService: NotificationMenuService = inject(NotificationMenuService);

  private _processService: ProcessService = inject(ProcessService);

  protected isProcessWheelSpinning: Signal<boolean | undefined> = toSignal(this._processService.hasRunningProcesses$);

  protected amountOfUnreadMessages: Signal<number | undefined> = toSignal(this._processService.amountOfUnreadProcesses$);

  private activePath: string | undefined = "";

  private readonly router: Router = inject(Router);

  private readonly _keycloakService: KeycloakService = inject(KeycloakService);

  // Todo implement the other consts for the roles. such as customer-roles, etc..
  buttons: BehaviorSubject<Button[]> = new BehaviorSubject<Button[]>([
    { defaultText: "Projekte", token: "header-tab-projects", path: "projects", permissions: [ { role: PROJECT_ROLES.tab, resource: PROJECT_ROLE_RESOURCE }, { role: PROJECT_ROLES.view, resource: PROJECT_ROLE_RESOURCE } ] },
    { defaultText: "Unternehmen", token: "header-tab-customers", path: "customers", permissions: [ { role: CUSTOMER_ROLES.view, resource: CUSTOMER_ROLE_RESOURCE } ] },
    { defaultText: "Verwaltung", token: "header-tab-administration", path: "administration", permissions: [ { role: ACCESS_CONTROL_ROLES.readerUnitView, resource: ACCESS_CONTROL_ROLE_RESOURCE } ] }
  ]);

  private readonly faIconLibrary: FaIconLibrary = inject(FaIconLibrary);

  constructor () {
    this.faIconLibrary.addIcons(faQuestionCircle, faBell);
  }

  ngOnInit (): void {
    this.setActiveNavButton();
    // This.resolvePermissions();
  }

  resolvePermissions (): void {
    const updatedButtons: Button[] = this.buttons.value.map((button: Button) => ({
      ...button,
      isDisabled: button.permissions?.some((p: Permission): boolean => !this._keycloakService.hasAccessTokenResourceAccess(p.resource, p.role))
    }));

    this.buttons.next(updatedButtons);
  }

  setActiveNavButton (): void {
    this.activePath = this.getFirstSegment(this.router.url);
    this.updateButtonState(this.activePath);
    this.router.events.pipe(
      filter((navigationEvent: Event): boolean => navigationEvent instanceof NavigationEnd),
      startWith(new NavigationEnd(0, this.router.url, this.router.url)),
      pairwise(),
      filter(([ previous, current ]: [Event, Event]) => {
        const prevPath: string | undefined = this.getFirstSegment((previous as NavigationEnd).url);
        const currPath: string | undefined = this.getFirstSegment((current as NavigationEnd).url);

        return prevPath !== currPath;
      })
    ).subscribe(() => {
      const currentPath: string | undefined = this.getFirstSegment(this.router.url);

      this.updateButtonState(currentPath);
    });
  }

  private updateButtonState (activePath: string | undefined): void {
    const updatedButtons: Button[] = this.buttons.value.map((button: Button) => ({
      ...button,
      isActive: button.path === activePath
    }));

    this.buttons.next(updatedButtons);
  }

  /* eslint-disable-next-line @tseslint/class-methods-use-this */
  getFirstSegment (url: string): string | undefined {
    return url.split("/")[ 1 ];
  }

  onButtonClick (path: string, mouseEvent: MouseEvent, isDisabled: boolean | undefined): void {
    if (isDisabled) {
      mouseEvent.stopPropagation();
      mouseEvent.preventDefault();

      return;
    }

    this.resolveNavigation(path);
  }

  resolveNavigation (path: string): void {
    void this.router.navigateByUrl(`/${ path }`);
  }

  /* eslint-disable-next-line @tseslint/class-methods-use-this */
  onHelpClick (): void {
    window.open("https://bclonsite.atlassian.net/servicedesk/customer/user/login?destination=portals", "_blank");
  }


  protected onActivityCenterClick (): void {
    this._notificationMenuService.requestMenuOpen();
  }
}
